import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  TrophyIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import api from '../utils/api'

export default function InstructorStudentDetail() {
  const { assignmentId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [student, setStudent] = useState(null)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const [scheduleData, setScheduleData] = useState({
    scheduled_at: '',
    meeting_link: '',
    notes: ''
  })
  const [completeData, setCompleteData] = useState({
    feedback: '',
    notes: ''
  })

  useEffect(() => {
    fetchStudentDetail()
  }, [assignmentId])

  const fetchStudentDetail = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/instructor/students/${assignmentId}`)
      setStudent(response.data)
    } catch (error) {
      console.error('Error fetching student detail:', error)
      alert('Failed to load student details')
    } finally {
      setLoading(false)
    }
  }

  const handleScheduleSession = (session) => {
    setSelectedSession(session)
    setScheduleData({
      scheduled_at: '',
      meeting_link: '',
      notes: ''
    })
    setShowScheduleModal(true)
  }

  const handleCompleteSession = (session) => {
    setSelectedSession(session)
    setCompleteData({
      feedback: '',
      notes: ''
    })
    setShowCompleteModal(true)
  }

  const submitSchedule = async () => {
    try {
      await api.post(`/instructor/sessions/${selectedSession.id}/schedule`, scheduleData)
      alert('Session scheduled successfully! Student will be notified via email.')
      setShowScheduleModal(false)
      fetchStudentDetail()
    } catch (error) {
      console.error('Error scheduling session:', error)
      alert('Failed to schedule session')
    }
  }

  const submitComplete = async () => {
    try {
      await api.post(`/instructor/sessions/${selectedSession.id}/complete`, completeData)
      alert('Session marked as complete! Student will be notified via email.')
      setShowCompleteModal(false)
      fetchStudentDetail()
    } catch (error) {
      console.error('Error completing session:', error)
      alert('Failed to complete session')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!student) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <p className="text-center text-gray-500">Student not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/instructor')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{student.student.name}</h1>
            <p className="text-gray-600 mt-1">{student.student.email}</p>
            <p className="text-sm text-gray-500 mt-1">
              Enrolled: {new Date(student.student.enrolled_at).toLocaleDateString()} • 
              Tier: <span className="font-medium capitalize">{student.student.enrolled_tier}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Assignment Status</p>
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mt-1">
              {student.assignment.status}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Modules Completed</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {student.progress.modules.filter(m => m.completed).length}/{student.progress.modules.length}
              </p>
            </div>
            <AcademicCapIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Submissions</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{student.progress.submissions.length}</p>
            </div>
            <DocumentTextIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Badges Earned</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{student.progress.badges.length}</p>
            </div>
            <TrophyIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Certificates</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{student.progress.certificates.length}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Sessions Management */}
      <div className="bg-white rounded-lg border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Elite Coaching Sessions</h2>
          <p className="text-sm text-gray-600 mt-1">Schedule and manage 1-on-1 sessions</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {student.sessions.map((session) => (
              <div 
                key={session.id}
                className={`border-2 rounded-lg p-4 ${
                  session.is_completed 
                    ? 'border-green-200 bg-green-50' 
                    : session.scheduled_at 
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{session.type_name}</h3>
                  {session.is_completed && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  )}
                  {session.scheduled_at && !session.is_completed && (
                    <ClockIcon className="h-5 w-5 text-yellow-600" />
                  )}
                </div>

                {session.scheduled_at && (
                  <div className="text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1 mb-1">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{new Date(session.scheduled_at).toLocaleString()}</span>
                    </div>
                    {session.meeting_link && (
                      <a 
                        href={session.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 underline"
                      >
                        Join Meeting
                      </a>
                    )}
                  </div>
                )}

                {session.is_completed && session.completed_at && (
                  <p className="text-sm text-gray-600 mb-2">
                    Completed: {new Date(session.completed_at).toLocaleDateString()}
                  </p>
                )}

                {session.notes && (
                  <p className="text-sm text-gray-600 mb-3">
                    <span className="font-medium">Notes:</span> {session.notes}
                  </p>
                )}

                <div className="flex gap-2">
                  {!session.is_completed && (
                    <>
                      <button
                        onClick={() => handleScheduleSession(session)}
                        className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                      >
                        {session.scheduled_at ? 'Reschedule' : 'Schedule'}
                      </button>
                      {session.scheduled_at && (
                        <button
                          onClick={() => handleCompleteSession(session)}
                          className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        >
                          Complete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Module Progress */}
      <div className="bg-white rounded-lg border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Module Progress</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Spent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Accessed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {student.progress.modules.map((module) => (
                <tr key={module.module_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {module.completed && <CheckCircleIcon className="h-5 w-5 text-green-600" />}
                      <span className="font-medium text-gray-900">{module.module_title}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${module.completion_percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{module.completion_percentage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {module.time_spent ? `${Math.round(module.time_spent / 60)} mins` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {module.last_accessed ? new Date(module.last_accessed).toLocaleDateString() : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Submissions */}
      <div className="bg-white rounded-lg border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Capstone Submissions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GitHub</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {student.progress.submissions.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    No submissions yet
                  </td>
                </tr>
              ) : (
                student.progress.submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {submission.capstone_title}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {submission.grade ? (
                        <span className="text-sm font-medium text-green-600">{submission.grade}</span>
                      ) : (
                        <span className="text-sm text-gray-400">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {submission.github_url && (
                        <a 
                          href={submission.github_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                        >
                          <CodeBracketIcon className="h-4 w-4" />
                          View Code
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Schedule Session Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Schedule {selectedSession.type_name}
              </h3>
              <button onClick={() => setShowScheduleModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={scheduleData.scheduled_at}
                  onChange={(e) => setScheduleData({...scheduleData, scheduled_at: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link (Zoom, Google Meet, etc.)
                </label>
                <input
                  type="url"
                  value={scheduleData.meeting_link}
                  onChange={(e) => setScheduleData({...scheduleData, meeting_link: e.target.value})}
                  placeholder="https://zoom.us/j/..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={scheduleData.notes}
                  onChange={(e) => setScheduleData({...scheduleData, notes: e.target.value})}
                  rows="3"
                  placeholder="Agenda or preparation notes..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitSchedule}
                  disabled={!scheduleData.scheduled_at || !scheduleData.meeting_link}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Schedule Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Session Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Complete {selectedSession.type_name}
              </h3>
              <button onClick={() => setShowCompleteModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Feedback (for student)
                </label>
                <textarea
                  value={completeData.feedback}
                  onChange={(e) => setCompleteData({...completeData, feedback: e.target.value})}
                  rows="4"
                  placeholder="What did you cover? Next steps for the student..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Private Notes (optional)
                </label>
                <textarea
                  value={completeData.notes}
                  onChange={(e) => setCompleteData({...completeData, notes: e.target.value})}
                  rows="3"
                  placeholder="Your private notes about the session..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitComplete}
                  disabled={!completeData.feedback}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
